import { Print, LocationOn } from '@mui/icons-material';
import {
	Card,
	CardContent,
	CardHeader,
	Chip,
	ButtonBase as OrgButtonBase,
	Stack,
	Theme,
	Typography,
	useTheme
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DefinitionList } from '../../../mui-custom/DefinitionList';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAccountDeactivatedCheck } from '../../../hooks/useAccountDeactivatedCheck';
import { dateTime } from '../../../lib/dateHelper';
import Page from '../../../mui-custom/Page/Page';
import { State } from '../../reducers';
import { IconicText } from '../../../components/IconicText';
import { CasaviCommunity } from '@api/v1';
import { AttachmentList, AttachmentThumbnail } from '../../../components/Attachment';
import { mapTicketStatusToColor } from '../../../lib/types';
import Circle from '@mui/icons-material/Circle';
import { PRIORITIES } from './AddCustomFieldsPage';

const ButtonBase = (props: { [x: string]: unknown }) => {
	const { accountDeactivated } = useAccountDeactivatedCheck();
	if (!accountDeactivated) return <OrgButtonBase {...props} />;
	return <OrgButtonBase {...props} disabled />;
};

const useStyles = makeStyles(({ spacing }: Theme) => ({
	headerButton: {
		padding: spacing(1)
	},
	iconText: {
		marginTop: spacing(0.5),
		marginBottom: spacing(0.5)
	}
}));

export function PriorityTag({ priority }: { priority: number }): JSX.Element {
	const { spacing } = useTheme();
	const { t } = useTranslation();
	return (
		<>
			{PRIORITIES.filter(({ value }) => value === priority).map(({ value, color, label }) => (
				<Stack key={value} flexDirection="row">
					<Circle color={color} sx={{ fontSize: spacing(2), color, mt: 0.5 }} />
					<Typography>&nbsp;{t(label)}</Typography>
				</Stack>
			))}
		</>
	);
}
export function DetailSubPage() {
	const { t } = useTranslation();
	const classes = useStyles();
	const { ticketDetails } = useSelector((state: State) => state.tickets);
	const { spacing } = useTheme();

	const mapStatusToLabel = {
		OPEN: t('tickets:status_open').toUpperCase(),
		INPROGRESS: t('tickets:status_inprogress').toUpperCase(),
		POSTPONED: t('tickets:status_postponed').toUpperCase(),
		CLOSED: t('tickets:status_closed').toUpperCase()
	};

	const getCasaviAddress = (community: CasaviCommunity) => {
		if (!community) return '';
		let addressString = '';
		if (community.Addresses.length > 0) {
			community.Addresses.forEach((address) => {
				addressString += `${address.street} `;
			});
		}
		addressString += `${community.postalCode} ${community.city} ${community.country}`;

		return addressString;
	};

	return (
		<Page
			id="ticket_details_container"
			headline={
				<>
					<Stack direction="row" sx={{ justifyContent: 'space-between' }}>
						<Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
							<span>{ticketDetails.title}</span>
						</Stack>
						<ButtonBase onClick={() => window.print()} className={classes.headerButton}>
							<Print />
						</ButtonBase>
					</Stack>
					<Stack direction="row" sx={{ justifyContent: 'space-between' }}>
						<Typography sx={{ marginTop: spacing(2) }}>
							<span>{`#${ticketDetails.identifier}`}</span>
							<Chip
								color={mapTicketStatusToColor[ticketDetails.status]}
								size="small"
								sx={{ borderRadius: 0.5, minWidth: spacing(11), ml: spacing(1) }}
								label={mapStatusToLabel[ticketDetails.status] ?? ticketDetails.status}
							/>
						</Typography>
						<Stack flexDirection="row" sx={{ mt: 2 }}>
							<Typography variant="h6">{t('tickets:priority')}:&nbsp;</Typography>
							<PriorityTag priority={ticketDetails.priority} />
						</Stack>
					</Stack>
				</>
			}
			text={`${t('tickets:detail_page_created_at_label')}: ${dateTime(
				ticketDetails.createdAt
			).toFormat('dd.MM.yyyy HH:mm')}`}
		>
			<Card>
				<CardHeader title={t('tickets:label_property')} />
				<DefinitionList
					sx={{ px: 2 }}
					items={[
						{
							key: t('tickets:label_property_manager'),
							value: ticketDetails.Tenant?.name
						},
						{
							key: t('tickets:label_property'),
							value: ticketDetails.Community?.name
						},
						{
							key: t('tickets:label_property_address'),
							value: (
								<IconicText
									className={classes.iconText}
									icon={<LocationOn />}
									text={
										<a
											href={`https://maps.google.com/?q=${getCasaviAddress(
												ticketDetails.Community
											)}`}
											target="_blank"
											dangerouslySetInnerHTML={{
												__html: getCasaviAddress(ticketDetails.Community)
											}}
										></a>
									}
								/>
							)
						}
					]}
				/>
			</Card>
			{ticketDetails.Attachments?.length > 0 && (
				<Card>
					<CardHeader title={t('tickets:detail_page_attachments')} />
					<CardContent>
						<AttachmentList>
							{(ticketDetails.Attachments || [{}]).map((attachment) => (
								<AttachmentThumbnail
									key={attachment.id}
									name={attachment.name}
									link={attachment.temporaryUrl}
									thumbnail={attachment.temporaryThumbUrl}
								/>
							))}
						</AttachmentList>
					</CardContent>
				</Card>
			)}
		</Page>
	);
}
